import { computed, ref, reactive } from 'vue'
import type { Category, Product, Variant } from '@scayle/storefront-nuxt'
import { useRoute } from '#app/composables/router'
import { useState } from '#app/composables/state'
import { buildSideNavigationStyle } from '#layers/base/utils/sideNavigationLayout'
import type { FlyoutMenuCategory } from '#layers/base/types/category'
import type { NavigationItem } from '#layers/base/types/navigation'
import type {
  ProductViewHistoryItem,
  PromotionInitialKeys,
} from '#layers/base/types/promotion'

const uiState = reactive({
  sideNavigationOpen: false,
  flyoutMenuOpen: false,
  flyoutMenuCategoryId: -1 as number | string,
  flyoutMenuCategories: [] as FlyoutMenuCategory[],
  searchFlyout: false,
  mobileSearchIsActive: false,
  basketFlyout: false,
  userFlyout: false,
  selectedSideNavigationEntry: undefined as Category | undefined,
  localeRedirectModal: false,
  productSizeGuideModal: false,
  shippingCostHintModal: false,
  activeVariant: undefined as Variant | undefined,
  activeProduct: undefined as Product | undefined,
  backInStockModal: false,
  addToBasketFlyout: false,
  lastVariantIdAddedToBasket: undefined as number | undefined,
  initialKeys: {} as PromotionInitialKeys,
  productViewHistory: [] as ProductViewHistoryItem[],
  lastInteractionType: 'mouse' as 'mouse' | 'keyboard',
})

const bannerHeight = ref(0)
const sideNavigationOffset = ref(0)
const showBackInStock = ref(false)

export default () => {
  const isMobileSearchBarVisible = useState('search-bar-visible', () => true)
  const route = useRoute()
  const isPdp = computed(() => route.path.includes('/p/'))

  // Side Navigation state (Mobile)
  const isSideNavigationOpen = computed(() => uiState.sideNavigationOpen)
  const closeSideNavigation = () => {
    uiState.sideNavigationOpen = false
  }
  const openSideNavigation = () => {
    uiState.sideNavigationOpen = true
  }

  const isLocaleRedirectModalOpen = computed(() => uiState.localeRedirectModal)

  const selectSideNavigationEntry = (entry: Category | undefined) => {
    uiState.selectedSideNavigationEntry = entry
  }

  // FlyoutMenu state
  const isFlyoutMenuOpen = computed(() => uiState.flyoutMenuOpen)

  const flyoutMenuCategory = computed(() =>
    uiState.flyoutMenuCategories.find(
      (c) => c.id === uiState.flyoutMenuCategoryId,
    ),
  )
  const flyoutMenuCategories = computed(() => uiState.flyoutMenuCategories)
  const flyoutMenuCategoryId = computed(() => uiState.flyoutMenuCategoryId)

  const closeFlyoutMenu = (event?: MouseEvent, force: boolean = false) => {
    const relatedTarget = event?.relatedTarget as Element
    if (
      ![
        'flyout-menu',
        'first-container-column',
        'second-container-column',
        'third-container-column',
        'flyout-menu-items-container',
        'flyout-overscroll-container',
        'flyout-menu-items-container-content',
      ].includes(relatedTarget?.id) ||
      force
    ) {
      uiState.flyoutMenuOpen = false
      uiState.flyoutMenuCategoryId = -1
    }
  }

  const openFlyoutMenu = (category: NavigationItem) => {
    uiState.flyoutMenuOpen = true
    // Update: Removed uiState.flyoutMenuCategoryId = Number(category.id)
    // Storyblok-only navigation items have a string id (storyblok _uid), therefore turning them into a number won't work
    uiState.flyoutMenuCategoryId = category.id
  }

  // Search Flyout
  const isSearchFlyoutOpen = computed(() => uiState.searchFlyout)

  const openSearchFlyout = () => {
    uiState.searchFlyout = true
  }

  const closeSearchFlyout = () => {
    uiState.searchFlyout = false
  }

  const openBasketFlyout = () => {
    uiState.basketFlyout = true
  }

  const closeBasketFlyout = () => {
    uiState.basketFlyout = false
  }

  const openAddToBasketFlyout = (
    lastVariantIdAddedToBasket: number | undefined,
  ) => {
    uiState.addToBasketFlyout = true
    uiState.lastVariantIdAddedToBasket = lastVariantIdAddedToBasket
  }

  const closeAddToBasketFlyout = () => {
    uiState.addToBasketFlyout = false
  }

  const openUserFlyout = () => {
    uiState.userFlyout = true
  }

  const closeUserFlyout = () => {
    uiState.userFlyout = false
  }

  const setFlyoutMenuCategories = (
    categories: NavigationItem[] | FlyoutMenuCategory[],
  ) => {
    uiState.flyoutMenuCategories = categories.map((c) => ({
      id: Number(c.id),
      name: c.name,
      path: c.path,
      slug: 'slug' in c ? c.slug : c.name,
      children: c.children || [],
    }))
  }

  // Mobile Search state
  const mobileSearchIsActive = computed(() => uiState.mobileSearchIsActive)

  const setMobileSearchIsActive = (value: boolean) =>
    (uiState.mobileSearchIsActive = value)

  // Product Size Guide Modal
  const isProductSizeGuideModalOpen = computed(
    () => uiState.productSizeGuideModal,
  )

  const openProductSizeGuide = () => {
    uiState.productSizeGuideModal = true
  }

  const closeProductSizeGuide = () => {
    uiState.productSizeGuideModal = false
  }

  // Shipping Hint Modal
  const isShippingHintCostModalOpen = computed(
    () => uiState.shippingCostHintModal,
  )

  const openShippingCostHint = () => {
    uiState.shippingCostHintModal = true
  }

  const closeShippingCostHint = () => {
    uiState.shippingCostHintModal = false
  }

  // Locale Redirect Modal
  const setLocaleRedirectModal = (value: boolean) => {
    uiState.localeRedirectModal = value
  }

  // Set active variant
  const setActiveVariant = (variant: Variant | undefined) => {
    uiState.activeVariant = variant
  }

  const setActiveProduct = (product: Product | undefined) => {
    uiState.activeProduct = product
  }

  const setProductInitialKeys = (
    masterKey: string | undefined,
    promotionKey: string | undefined,
  ) => {
    uiState.initialKeys.masterKey = masterKey
    uiState.initialKeys.promotionKey = promotionKey
  }

  const setProductViewHistory = (orderNumber: string, promotionKey: string) => {
    const existingIndex = uiState.productViewHistory.findIndex(
      (item) => item.orderNumber === orderNumber,
    )

    const newItem = {
      orderNumber,
      promotionKey,
    }

    if (existingIndex !== -1) {
      uiState.productViewHistory.splice(existingIndex, 1, newItem)
    } else {
      uiState.productViewHistory.push(newItem)
    }
  }

  // Used to focus the first focusable element when the flyout menu is opened with keyboard
  const setLastInteractionType = (type: 'mouse' | 'keyboard') => {
    uiState.lastInteractionType = type
  }

  const openBackInStockModal = () => {
    showBackInStock.value = true
  }

  const closeBackInStockModal = () => {
    showBackInStock.value = false
  }

  return {
    isPdp,
    isSideNavigationOpen,
    closeSideNavigation,
    openSideNavigation,
    selectSideNavigationEntry,
    isFlyoutMenuOpen,
    flyoutMenuCategory,
    flyoutMenuCategories,
    flyoutMenuCategoryId,
    setFlyoutMenuCategories,
    closeFlyoutMenu,
    openFlyoutMenu,
    isSearchFlyoutOpen,
    closeSearchFlyout,
    openSearchFlyout,
    mobileSearchIsActive,
    setMobileSearchIsActive,
    selectedSideNavigationEntry: computed(
      () => uiState.selectedSideNavigationEntry,
    ),
    isBasketFlyoutOpen: computed(() => uiState.basketFlyout),
    isUserFlyoutOpen: computed(() => uiState.userFlyout),
    openBasketFlyout,
    closeBasketFlyout,
    openUserFlyout,
    closeUserFlyout,
    isLocaleRedirectModalOpen,
    setLocaleRedirectModal,
    isProductSizeGuideModalOpen,
    openProductSizeGuide,
    closeProductSizeGuide,
    isShippingHintCostModalOpen,
    openShippingCostHint,
    closeShippingCostHint,
    setActiveVariant,
    setActiveProduct,
    activeVariant: computed(() => uiState.activeVariant as Variant),
    activeProduct: computed(() => uiState.activeProduct as Product),
    isMobileSearchBarVisible,
    bannerHeight,
    sideNavigationOffset,
    // Both properties come off the one measured offset so they cannot drift
    // apart again: a container starting at `offset` has to be `100vh - offset`
    // tall to end exactly at the fold (ECOM-2796).
    sideNavigationStyle: computed(() =>
      buildSideNavigationStyle(sideNavigationOffset.value),
    ),
    showBackInStock,
    openBackInStockModal,
    closeBackInStockModal,
    isAddToBasketFlyoutOpen: computed(() => uiState.addToBasketFlyout),
    openAddToBasketFlyout,
    closeAddToBasketFlyout,
    lastVariantIdAddedToBasket: computed(
      () => uiState.lastVariantIdAddedToBasket,
    ),
    setProductInitialKeys,
    productInitialKeys: computed(() => uiState.initialKeys),
    productViewHistory: computed(() => uiState.productViewHistory),
    setProductViewHistory,
    setLastInteractionType,
    lastInteractionType: computed(() => uiState.lastInteractionType),
  }
}
